import { Link, useRouterState } from '@tanstack/react-router';
import {
  Building2,
  ChevronDown,
  Dumbbell,
  Globe,
  LayoutGrid,
  Mail,
  Palette,
  RefreshCw,
  Trophy,
  UserCog,
  Users,
  Whistle,
  type LucideIcon,
} from 'lucide-react';
import { useRef, useState } from 'react';
import { useAuth } from '../lib/auth';
import { useClickOutside } from '../lib/use-click-outside';

export const ORG_TABS: Array<{
  to: string;
  label: string;
  perm:
    | 'teams.sync'
    | 'training.view'
    | 'members.view'
    | 'season.view'
    | 'trainers.manage'
    | null;
  icon: LucideIcon;
}> = [
  { to: '/organization', label: 'Vereinsdaten', perm: null, icon: Building2 },
  { to: '/members', label: 'Mitglieder', perm: 'members.view', icon: Users },
  { to: '/organization/design', label: 'Design', perm: null, icon: Palette },
  { to: '/organization/auth-design', label: 'Design Anmelde/Registerseite', perm: null, icon: Palette },
  { to: '/organization/custom-domain', label: 'Eigene Domain', perm: null, icon: Globe },
  { to: '/organization/navigation', label: 'Navigationsleiste', perm: null, icon: LayoutGrid },
  { to: '/organization/groups', label: 'Trainingsgruppen', perm: 'training.view', icon: Dumbbell },
  { to: '/organization/seasons', label: 'Saisons', perm: 'season.view', icon: Trophy },
  { to: '/organization/user-management', label: 'Benutzerverwaltung', perm: 'members.view', icon: UserCog },
  { to: '/organization/mail', label: 'SMTP Server', perm: null, icon: Mail },
  { to: '/organization/turnier-de', label: 'Turnier.de Sync', perm: 'teams.sync', icon: RefreshCw },
  { to: '/organization/trainers', label: 'Trainer', perm: 'trainers.manage', icon: Whistle },
];

/** Sub-navigation for the "Verein" admin area (Vereinsdaten / Design / Turnier.de Sync) —
 * on desktop this same list appears nested under "Verein" in the sidebar
 * (`AppShell.tsx`), so this in-page control only renders on mobile, where the
 * sidebar isn't shown. A single dropdown button showing the active section
 * (§ Nutzeranfrage: "modern und übersichtlich") — replaces an earlier
 * horizontally-scrolling pill row, which hid most of the ~13 sections behind
 * a scroll-and-hunt interaction with no obvious "there's more here" signal
 * beyond a soft fade at the edge. The dropdown surfaces the full list in one
 * tap, same "speed-dial" chrome (icon-in-circle rows, click-outside to
 * close) already established for the mobile "Verwaltung" menu in
 * `AppShell.tsx`, so both feel like the same design system. */
export function OrgTabs() {
  const { can } = useAuth();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  useClickOutside(rootRef, () => setOpen(false), open);

  const visibleTabs = ORG_TABS.filter((t) => !t.perm || can(t.perm));
  const active = visibleTabs.find((t) => t.to === path) ?? visibleTabs[0];
  const ActiveIcon = active?.icon ?? Building2;

  return (
    <div className="relative mb-4 md:hidden" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="listbox"
        className="flex w-full items-center gap-2.5 rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-left shadow-sm transition-colors active:bg-slate-50"
      >
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-accent/10 text-brand-accent">
          <ActiveIcon size={16} />
        </span>
        <span className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-800">{active?.label}</span>
        <ChevronDown size={18} className={`shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          role="listbox"
          className="org-tabs-dropdown absolute left-0 right-0 z-30 mt-1.5 max-h-[70vh] overflow-y-auto rounded-xl border border-slate-200 bg-white p-1.5 shadow-xl"
        >
          {visibleTabs.map((t) => {
            const Icon = t.icon;
            const isActive = t.to === path;
            return (
              <Link
                key={t.to}
                to={t.to}
                role="option"
                aria-selected={isActive}
                onClick={() => setOpen(false)}
                className={`flex items-center gap-2.5 rounded-lg px-2.5 py-2.5 text-sm font-medium transition-colors ${
                  isActive ? 'bg-brand-accent/10 text-brand-accent' : 'text-slate-700 hover:bg-slate-50 active:bg-slate-100'
                }`}
              >
                <span
                  className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${
                    isActive ? 'bg-brand-accent text-white' : 'bg-slate-100 text-slate-400'
                  }`}
                >
                  <Icon size={14} />
                </span>
                {t.label}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
